import React, { useEffect, useState } from 'react';
import clsx from 'clsx';

import { makeStyles } from 'tss-react/mui';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import Sidebar from '../../Navigation/SideBar/SideBar';
import TopMenu from '../TopMenu/TopMenu';
import Banner from '../Feedback/Banner/Banner';
import { getCategories } from '../../../slices/itemsSlice';
import { getOrganization, getSubscription } from '../../../slices/organizationSlice';
import { getCustomers } from '../../../slices/customersSlice';

const useStyles = makeStyles()((theme) => ({

  root: {
    // La barre est FIXE : sans ce décalage, elle passe par-dessus le contenu.
    // Les 56/64 px sont sa hauteur (`theme.mixins.toolbar`), et le `spacing(3)`
    // est de l'air — collé à la barre, un titre se lit mal même sans être
    // recouvert.
    // ⚠️ `calc`, et pas une addition : `theme.spacing(3)` rend la CHAÎNE
    // « 24px » en MUI v5, donc « 56 + theme.spacing(3) » vaut « 5624px ».
    paddingTop: `calc(56px + ${theme.spacing(3)})`,
    height: '100%',
    [theme.breakpoints.up('sm')]: {
      paddingTop: `calc(64px + ${theme.spacing(3)})`,
    },
  },
  shiftContent: {
    paddingLeft: 240,
  },
  content: {
    height: '100%',
  },
}));

export default function MainLayout({ children = {} }) {
  // 🔴 Déstructurer. `tss-react` rend « {classes, cx, css, theme} », là où
  // `makeStyles` de MUI v4 rendait les classes directement. Sans les
  // accolades, `classes.root` valait `undefined` : le décalage de la barre
  // n'était appliqué NULLE PART, et la barre fixe recouvrait de 7 px les
  // titres de « Membres », « Prêts » et « Catégories ». Le build passait —
  // `clsx` accepte `undefined` et produit une classe nommée « undefined ».
  const {classes} = useStyles();
  // Le menu latéral était PERSISTANT au-delà de 1920 px : il occupait 240 px en
  // permanence, sans moyen de le refermer, et il encombrait la lecture de la
  // collection sur les grands écrans. Il est désormais escamotable à toutes les
  // tailles : on l'ouvre par le bouton du bandeau, on le referme en cliquant à côté.
  const isDesktop = false;

  const dispatch = useDispatch();

  const { isLoggedIn } = useSelector((state) => state.auth);

  useEffect(() => {
    if (isLoggedIn) {
      dispatch(getCategories());
    }
  }, [dispatch, isLoggedIn]);

  useEffect(() => {
    if (isLoggedIn) {
      dispatch(getCustomers());
    }
  }, [dispatch, isLoggedIn]);

  useEffect(() => {
    if (isLoggedIn) {
      dispatch(getSubscription());
    }
  }, [dispatch, isLoggedIn]);

  useEffect(() => {
    if (isLoggedIn) {
      dispatch(getOrganization());
    }
  }, [dispatch, isLoggedIn]);

  const [openSidebar, setOpenSidebar] = useState(false);

  const handleSidebarOpen = () => {
    setOpenSidebar(true);
  };

  const handleSidebarClose = () => {
    setOpenSidebar(false);
  };

  const shouldOpenSidebar = isDesktop ? true : openSidebar;

  return (
    <div
      className={clsx({
        [classes.root]: true,
        [classes.shiftContent]: isDesktop,
      })}
    >

      <TopMenu onSidebarOpen={handleSidebarOpen} minimalLayout={false} />
      <Sidebar
        onClose={handleSidebarClose}
        open={shouldOpenSidebar}
        variant={isDesktop ? 'persistent' : 'temporary'}
      />

      <main className={classes.content}>
        <Banner />
        {children}
      </main>

    </div>
  );
}

MainLayout.propTypes = {
  children: PropTypes.node,
};
